import { useState } from 'react';
import { temaDeLaPuerta } from './medidas';

// Se reexporta para que cada puerta importe de un solo sitio.
export { CLASE_CAMPO } from './medidas';

/* =====================================================================
   LAS PUERTAS · lo que comparten las tres pantallas de acceso
   =====================================================================
   Hay tres puertas en TEGO —entrar, darse de alta, y la del operador— y
   se habían resuelto por separado tres veces: alturas de campo
   distintas, radios distintos, el bloque flotando arriba en unas y
   centrado en otras. Aquí vive lo que las hace hermanas, y cada pantalla
   pone encima su carácter.

   LA CONSOLA DE OPERADOR NO PUEDE IMPORTAR ESTO: es HTML y CSS a pelo,
   sin React, por su propia política de contenido. Así que comparte las
   MEDIDAS, no el código — están escritas aquí abajo y repetidas en
   `consola/estatico/consola.css` con una nota que apunta a este archivo.
   Si una cambia, cambian las dos.

       campo          48 px de alto, radio 4, borde de 1 px que se ve
       botón          48 px, radio 4, color sólido a plena fuerza
       etiqueta       13 px, semibold, 6 px de aire debajo
       panel visual   55 % / 45 %, y desaparece por debajo de 1024 px
       reparto        marca arriba · frase al centro · pie abajo

   EL BOTÓN PRINCIPAL NO ARRANCA APAGADO. Es el elemento más fuerte de la
   pantalla y tiene que verse así desde el primer segundo; un botón lavado
   nada más abrir dice «esto no va a funcionar». Se apaga al 40 % —del
   mismo color, nunca gris cemento— solo cuando ya hay un motivo que
   contar: se intentó enviar y falta algo, o se está enviando.

   Y NO SE REGAÑA POR ADELANTADO. Ningún error se escribe hasta el primer
   intento de enviar. Es la misma corrección que se hizo en la consola de
   operador, traída aquí.
   ===================================================================== */

/* --- EL MARCO · dos paneles ----------------------------------------- */
export function MarcoPuerta({ panel, children }) {
  return (
    <div className="publico min-h-dvh w-full flex bg-[var(--p-fondo)] text-[var(--p-tinta)] font-sans"
         data-puerta={temaDeLaPuerta()}>
      {panel}
      {/* CENTRADO VERTICAL DE VERDAD. `justify-center` sobre una columna
          que puede crecer más que la pantalla dejaba el bloque pegado
          arriba con un hueco enorme debajo. `my-auto` centra cuando cabe
          y deja desplazar cuando no, que es lo que hace falta en un
          teléfono con el teclado abierto. */}
      <main className="w-full lg:w-[45%] flex flex-col overflow-y-auto px-6 py-10 sm:px-12 lg:px-14">
        <div className="w-full max-w-[24rem] mx-auto my-auto">{children}</div>
      </main>
    </div>
  );
}

/* --- EL PANEL VISUAL · marca arriba, frase al centro, pie abajo ------
   Con una rejilla de tres filas `auto 1fr auto` y NO con
   `space-between`: el reparto de espacio libre no centra nada cuando los
   tres bloques miden distinto, y la frase acababa a media altura por
   accidente. Aquí la fila de en medio se lleva todo el sobrante y la
   frase se centra dentro de ella. */
export function PanelVisual({ marca, frase, pie, fondo, halo, textura }) {
  return (
    <aside
      className="hidden lg:grid lg:w-[55%] relative overflow-hidden p-14 select-none
                 text-[var(--p-sobre-noche)]"
      style={{ background: fondo, gridTemplateRows: 'auto 1fr auto' }}
      aria-hidden="true"
    >
      <div className="absolute inset-0" style={{ background: halo }} />
      {textura}
      <div className="relative">{marca}</div>
      <div className="relative flex items-center">
        <p
          className="max-w-[15ch] leading-[1.08] tracking-[-0.02em] text-balance"
          style={{
            fontFamily:
              'ui-serif, "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "DejaVu Serif", serif',
            fontSize: 'clamp(2.5rem, 3.1vw, 3rem)',
            fontWeight: 400,
          }}
        >
          {frase}
        </p>
      </div>
      <div className="relative">{pie}</div>
    </aside>
  );
}

/* --- LA TEXTURA · dibujada, nunca traída ---------------------------- */
export function TexturaPuntos({ id }) {
  return (
    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice"
         aria-hidden="true" focusable="false">
      <defs>
        <pattern id={`p-${id}`} width="26" height="26" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.5" fill="#ffffff" fillOpacity="0.085" />
        </pattern>
        <radialGradient id={`d-${id}`} cx="30%" cy="40%" r="78%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <mask id={`m-${id}`}>
          <rect width="100%" height="100%" fill={`url(#d-${id})`} />
        </mask>
      </defs>
      <rect width="100%" height="100%" fill={`url(#p-${id})`} mask={`url(#m-${id})`} />
      <g fill="none" stroke="#ffffff" strokeOpacity="0.10">
        <circle cx="86%" cy="112%" r="200" />
        <circle cx="86%" cy="112%" r="310" />
        <circle cx="86%" cy="112%" r="420" strokeOpacity="0.06" />
      </g>
    </svg>
  );
}

export function CampoPuerta({
  id, etiqueta, ayuda, error, opcional, children,
}) {
  return (
    <div>
      <label htmlFor={id}
             className="flex items-baseline gap-2 text-[0.8125rem] font-semibold text-[var(--p-tinta)] mb-1.5">
        {etiqueta}
        {opcional && (
          <span className="font-normal text-[var(--p-tinta-3)]">opcional</span>
        )}
      </label>
      {children}
      {/* AYUDA Y ERROR SON MICRO-COPIA (13 px, `data-micro`): la frase de
          una línea pegada a un campo y el aviso de validación no son
          «cuerpo» a efectos de M5, que pide 16 px para el texto de
          contenido y para los CAMPOS —no para la letra chica del campo—.
          El marcador lo respeta por el `data-micro`, con un piso de 12 px
          para que una regresión a ilegible sí salte. El teléfono no zumbea
          el zoom con esto: sólo lo hace al TOCAR un campo < 16 px. */}
      {error ? (
        <p role="alert" data-micro className="mt-1.5 text-[0.8125rem] text-[var(--p-error)]">{error}</p>
      ) : (
        ayuda && <p data-micro className="mt-1.5 text-[0.8125rem] text-[var(--p-tinta-3)]">{ayuda}</p>
      )}
    </div>
  );
}

/* EL BOTÓN PRINCIPAL. A plena fuerza salvo que haya un motivo que
   contar; y cuando lo hay, el MISMO color al 40 %, jamás gris. */
export function BotonPuerta({ apagado, children, ...resto }) {
  return (
    <button
      type="submit"
      aria-disabled={apagado || undefined}
      className={
        'w-full h-12 rounded text-[0.9375rem] font-semibold text-white ' +
        'bg-[var(--p-marca-solido)] hover:bg-[var(--p-marca-solido-fuerte)] ' +
        'transition-colors duration-150 inline-flex items-center justify-center gap-2 ' +
        (apagado ? 'opacity-40 cursor-not-allowed hover:bg-[var(--p-marca-solido)]' : '')
      }
      {...resto}
    >
      {children}
    </button>
  );
}

/* --- EL AVISO DE ERROR DEL SERVIDOR --------------------------------- */
export function AvisoError({ children }) {
  if (!children) return null;
  return (
    // EL AVISO DEL SERVIDOR ES CUERPO, NO MICRO-COPIA: es el mensaje que
    // dice qué pasó y qué hacer, y se lee entero. Va a 16 px (`text-base`,
    // era `text-sm` = 14) para cumplir M5 sin `data-micro`.
    <p role="alert"
       className="text-base leading-relaxed text-[var(--p-error)] bg-[var(--p-error-flojo)]
                  border border-[var(--p-error-borde)] rounded p-3">
      {children}
    </p>
  );
}

/* Un enlace que despliega lo largo en vez de tenerlo siempre en pantalla. */
export function Plegado({ resumen, children }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <>
      {/* M4 · BLANCO TÁCTIL DE 44 px. `inline-flex items-center min-h-11`
          da al toggle los 44 px de alto que pide M4 sin agrandar la letra
          (sigue en 13 px) ni empujar el texto. */}
      <button type="button" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}
              className="mt-1.5 inline-flex items-center min-h-11 text-[0.8125rem] font-semibold text-[var(--p-marca)] hover:underline">
        {resumen}
      </button>
      {abierto && (
        // LO PLEGADO ES MICRO-COPIA (13 px, `data-micro`): son las notas
        // secundarias que se despliegan —cómo recuperar la contraseña, el
        // RFC opcional—, no el cuerpo principal de la puerta. Un CAMPO
        // dentro de aquí (el RFC) sigue obligado a 16 px: el marcador
        // exime la letra pequeña, nunca los campos.
        <div data-micro className="mt-1.5 text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)]">
          {children}
        </div>
      )}
    </>
  );
}
